/**
 * Cross-Tab Sync via BroadcastChannel
 *
 * Provides direct browser-tab-to-tab data sharing for KPI store updates.
 * When a user edits schedule or finance data on one tab, the changes are
 * broadcast to all other tabs of the same origin instantly — no SignalR
 * round-trip or API refetch required.
 *
 * SignalR remains the authoritative path for cross-MACHINE sync (different
 * users or devices). BroadcastChannel supplements it for same-browser sync
 * where the timing of SignalR → backend commit → API refetch can cause
 * stale-data races.
 *
 * Architecture:
 *   Tab A (editor) ─► BroadcastChannel ─► Tab B, C (viewers)
 *     1. User edits → store updated → broadcastScheduleUpdate() called
 *     2. Other tabs receive message → hydrate store → dispatch event → re-score
 *
 * Guards:
 *   - Messages carry a unique senderId so a tab ignores its own broadcasts.
 *   - Each message has a monotonic timestamp; stale messages are dropped.
 */

import {
  scheduleSeriesStore,
  budgetSeriesStore,
  storeKey,
} from "../Data/store";
import type { ScheduleProgressPoint } from "../Data/ScheduleProgress/scheduleProgressOverrides";
import type { FinanceCostPoint } from "../Data/Budget/budgetOverrides";

// ── Channel setup ────────────────────────────────────────────

const CHANNEL_NAME = "l4e_cross_tab_sync";

/** Unique ID for this tab — used to ignore own broadcasts. */
const SENDER_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** The shared BroadcastChannel (lazily initialized). */
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null; // unsupported browser
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      return null;
    }
  }
  return channel;
}

// ── Message types ────────────────────────────────────────────

interface ScheduleUpdateMessage {
  type: "schedule_update";
  senderId: string;
  timestamp: number;
  projectGuid: string;
  instanceGuid: string | undefined;
  points: ScheduleProgressPoint[];
}

interface FinanceUpdateMessage {
  type: "finance_update";
  senderId: string;
  timestamp: number;
  projectGuid: string;
  instanceGuid: string | undefined;
  points: FinanceCostPoint[];
}

type SyncMessage = ScheduleUpdateMessage | FinanceUpdateMessage;

// ── Broadcast (sender side) ──────────────────────────────────

/**
 * Broadcast schedule series data to other tabs.
 * Called from scheduleProgressOverrides after a local write.
 */
export function broadcastScheduleUpdate(
  projectGuid: string,
  instanceGuid: string | undefined,
  points: ScheduleProgressPoint[],
): void {
  const ch = getChannel();
  if (!ch) return;
  try {
    const msg: ScheduleUpdateMessage = {
      type: "schedule_update",
      senderId: SENDER_ID,
      timestamp: Date.now(),
      projectGuid,
      instanceGuid,
      points,
    };
    ch.postMessage(msg);
  } catch {
    // Ignore errors (e.g., message too large, channel closed)
  }
}

/**
 * Broadcast finance series data to other tabs.
 * Called from budgetOverrides after a local write.
 */
export function broadcastFinanceUpdate(
  projectGuid: string,
  instanceGuid: string | undefined,
  points: FinanceCostPoint[],
): void {
  const ch = getChannel();
  if (!ch) return;
  try {
    const msg: FinanceUpdateMessage = {
      type: "finance_update",
      senderId: SENDER_ID,
      timestamp: Date.now(),
      projectGuid,
      instanceGuid,
      points,
    };
    ch.postMessage(msg);
  } catch {
    // Ignore errors
  }
}

// ── Receive (listener side) ──────────────────────────────────

/** Last-seen timestamp per store key — prevents processing stale messages. */
const lastSeenTs = new Map<string, number>();

/**
 * Start listening for cross-tab sync messages.
 * Call once in App.tsx on mount. Returns a cleanup function for unmount.
 *
 * When a message arrives from another tab:
 *   1. Updates the in-memory store directly (scheduleSeriesStore / budgetSeriesStore)
 *   2. Dispatches the appropriate CustomEvent so version bumps + useMemo rerun
 */
export function startCrossTabListener(): () => void {
  const ch = getChannel();
  if (!ch) return () => {};

  const handler = (event: MessageEvent<SyncMessage>) => {
    const msg = event.data;
    if (!msg || !msg.type || msg.senderId === SENDER_ID) return; // ignore own messages

    const k = storeKey(msg.projectGuid, msg.instanceGuid);

    // Drop stale messages (out-of-order delivery)
    const prev = lastSeenTs.get(k) ?? 0;
    if (msg.timestamp <= prev) return;
    lastSeenTs.set(k, msg.timestamp);

    if (msg.type === "schedule_update") {
      // Update store directly — no API round-trip needed
      const sorted = [...msg.points].sort((a, b) =>
        a.reporting_date.localeCompare(b.reporting_date)
      );
      scheduleSeriesStore.set(k, sorted);

      // Dispatch event to trigger version bump + useMemo rerun.
      // No `source` in detail → version bump listener accepts it.
      try {
        window.dispatchEvent(
          new CustomEvent("l4e_schedule_progress_changed", {
            detail: {
              project_guid: msg.projectGuid,
              source: "cross_tab",
            },
          })
        );
      } catch { /* ignore */ }
    } else if (msg.type === "finance_update") {
      const sorted = [...msg.points].sort((a, b) =>
        a.reporting_date.localeCompare(b.reporting_date)
      );
      budgetSeriesStore.set(k, sorted);

      try {
        window.dispatchEvent(
          new CustomEvent("l4e_finance_cost_changed", {
            detail: {
              project_guid: msg.projectGuid,
              source: "cross_tab",
            },
          })
        );
      } catch { /* ignore */ }
    }
  };

  ch.addEventListener("message", handler);
  return () => {
    ch.removeEventListener("message", handler);
  };
}

/**
 * Close the BroadcastChannel. Call on app teardown.
 */
export function closeCrossTabChannel(): void {
  if (channel) {
    try { channel.close(); } catch { /* ignore */ }
    channel = null;
  }
}
